import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './hooks/useAuth';
import { ToastProvider } from './hooks/useToast';
import { TimerProvider } from './hooks/useTimerStore';
import { useTheme, applyFirmBrandOverrides, setFirmEnforced } from './hooks/useTheme';
import { useFirmBranding } from './hooks/useAdmin';
import ToastContainer from './components/ui/Toast/ToastContainer';
import ErrorBoundary from './components/layout/ErrorBoundary';
import AuthGuard from './components/layout/AuthGuard';
import AdminGuard from './components/layout/AdminGuard';
import DashboardGuard from './components/layout/DashboardGuard';
import AppLayout from './components/layout/AppLayout';
import PreferencesPage from './pages/PreferencesPage/PreferencesPage';
import HomePage from './pages/HomePage/HomePage';
import DashboardPage from './pages/DashboardPage/DashboardPage';
import NewRecordPage from './pages/NewRecordPage/NewRecordPage';
import ContactPage from './pages/ContactPage/ContactPage';
import CasesPage from './pages/CasesPage/CasesPage';
import CaseFilePage from './pages/CaseFilePage/CaseFilePage';
import PermissionBlockedPage from './pages/PermissionBlockedPage/PermissionBlockedPage';
import ExplorerPage from './pages/ExplorerPage/ExplorerPage';
import AdminPage from './pages/AdminPage/AdminPage';
import {
  AdminOverview,
  UserManagement,
  PipelineManagement,
  ConfigureFields,
  DefendantData,
  IssueTickets,
  DataImport,
  DocumentUpload,
  DocumentLedger,
  PresetReports,
  RecycleBin,
  DraftTemplates,
  EmailTemplates,
  BulkLetters,
  AutomationsList,
  Integrations,
  DataQuality,
  ClientPortal,
  BulkReassign,
  SystemConfig,
  TaskTemplates,
  BillingOperations,
  FirmBranding,
} from './pages/AdminPage/sections';
import AutomationEditor from './pages/AutomationEditor/AutomationEditor';
import DashboardBuilderPage from './pages/DashboardBuilderPage/DashboardBuilderPage';
import DashboardListPage from './pages/DashboardBuilderPage/DashboardListPage';
import DashboardViewPage from './pages/DashboardBuilderPage/DashboardViewPage';
import EmbedDashboardPage from './pages/DashboardBuilderPage/EmbedDashboardPage';
import TasksPage from './pages/TasksPage/TasksPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

/**
 * Reads firm branding from React Query and applies (or removes) brand colour
 * overrides on :root whenever the data changes. Renders nothing — side-effects only.
 * Works for any authenticated user because GET /api/admin/firm-branding is auth-only
 * (not admin-only). Silently no-ops on network errors.
 */
function FirmBrandEffect() {
  const { data: branding } = useFirmBranding();
  useEffect(() => {
    if (!branding) return;
    const enforce = branding.brand_enforce_theme === 'true';
    setFirmEnforced(enforce);
    applyFirmBrandOverrides(branding.brand_primary_color, branding.brand_accent_color, enforce);
  }, [branding]);
  return null;
}

/**
 * All authenticated routes wrapped with AuthGuard + AppLayout.
 * Extracted so the top-level <Routes> can have a public embed route
 * that renders without authentication.
 */
function AuthGuardedApp() {
  return (
    <AuthGuard>
      <FirmBrandEffect />
      <Routes>
        <Route element={<AppLayout />}>
          <Route
            index
            element={
              <ErrorBoundary section="Home">
                <HomePage />
              </ErrorBoundary>
            }
          />
          <Route
            path="dashboard"
            element={
              <ErrorBoundary section="Dashboard">
                <DashboardGuard>
                  <DashboardPage />
                </DashboardGuard>
              </ErrorBoundary>
            }
          />
          <Route
            path="cases"
            element={
              <ErrorBoundary section="Cases">
                <CasesPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="tasks"
            element={
              <ErrorBoundary section="Tasks">
                <TasksPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="new-record"
            element={
              <ErrorBoundary section="New Record">
                <NewRecordPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="contacts/:id"
            element={
              <ErrorBoundary section="Contact">
                <ContactPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="cases/:id"
            element={
              <ErrorBoundary section="Case File">
                <CaseFilePage />
              </ErrorBoundary>
            }
          />
          <Route
            path="cases/:id/explorer"
            element={
              <ErrorBoundary section="Explorer">
                <ExplorerPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="admin"
            element={
              <ErrorBoundary section="Admin">
                <AdminGuard>
                  <AdminPage />
                </AdminGuard>
              </ErrorBoundary>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="pipelines" element={<PipelineManagement />} />
            <Route path="fields" element={<ConfigureFields />} />
            <Route path="defendants" element={<DefendantData />} />
            <Route path="tickets" element={<IssueTickets />} />
            <Route path="data-import" element={<DataImport />} />
            <Route path="document-upload" element={<DocumentUpload />} />
            <Route path="ledger" element={<DocumentLedger />} />
            <Route path="reports" element={<PresetReports />} />
            <Route path="recycle-bin" element={<RecycleBin />} />
            <Route path="draft-templates" element={<DraftTemplates />} />
            <Route path="email-templates" element={<EmailTemplates />} />
            <Route path="blp" element={<BulkLetters />} />
            <Route path="automations" element={<AutomationsList />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="data-quality" element={<DataQuality />} />
            <Route path="client-portal" element={<ClientPortal />} />
            <Route path="bulk-reassign" element={<BulkReassign />} />
            <Route path="system-config" element={<SystemConfig />} />
            <Route path="task-templates" element={<TaskTemplates />} />
            <Route path="billing-operations" element={<BillingOperations />} />
            <Route path="firm-branding" element={<FirmBranding />} />
          </Route>
          <Route
            path="admin/automations/:id"
            element={
              <ErrorBoundary section="Automation Editor">
                <AdminGuard>
                  <AutomationEditor />
                </AdminGuard>
              </ErrorBoundary>
            }
          />
          <Route
            path="preferences"
            element={
              <ErrorBoundary section="Preferences">
                <PreferencesPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="bi"
            element={
              <ErrorBoundary key="bi-list" section="BI Dashboards">
                <DashboardListPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="bi/demo"
            element={
              <ErrorBoundary key="bi-demo" section="BI Dashboard Demo">
                <DashboardBuilderPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="bi/:slug"
            element={
              <ErrorBoundary key="bi-view" section="BI Dashboard">
                <DashboardViewPage />
              </ErrorBoundary>
            }
          />
          <Route path="permission-blocked" element={<PermissionBlockedPage />} />
          {/* Cognito redirects to /home.html — catch it and go to / */}
          <Route path="home.html" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthGuard>
  );
}

export default function App() {
  // Apply saved colour palette on mount (persisted in localStorage)
  useTheme();

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <TimerProvider>
              <ToastContainer />
              <ErrorBoundary section="Application">
                <Routes>
                  {/* Public embed route — no auth required */}
                  <Route
                    path="bi/embed/:token"
                    element={
                      <ErrorBoundary key="bi-embed" section="BI Embed">
                        <EmbedDashboardPage />
                      </ErrorBoundary>
                    }
                  />
                  {/* All other routes require authentication */}
                  <Route path="*" element={<AuthGuardedApp />} />
                </Routes>
              </ErrorBoundary>
            </TimerProvider>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
